import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { addRecent } from "@/lib/recent";
import type { AirportSearchResult } from "@/lib/flightApi";
import { parseQuery, navigateForQuery } from "@/lib/parseQuery";
import { cn } from "@/lib/utils";

type Props = {
  autoFocus?: boolean;
  size?: "default" | "compact";
  className?: string;
};

export function FlightSearchForm({ autoFocus = false, size = "default", className }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation("home");
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState<AirportSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const reqId = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!autoFocus || typeof window === "undefined") return;
    if (window.matchMedia("(min-width: 768px)").matches) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const text = q.trim();
    const parsed = parseQuery(text);
    if (parsed.kind !== "airport-search" || text.length < 2) {
      setSuggestions([]);
      return;
    }
    const id = ++reqId.current;
    const timer = setTimeout(async () => {
      try {
        const { searchAirports } = await import("@/lib/flightApi");
        const r = await searchAirports(text);
        if (id === reqId.current) setSuggestions(r.slice(0, 5));
      } catch {
        if (id === reqId.current) setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [q]);

  function submit(e?: FormEvent) {
    e?.preventDefault();
    const parsed = parseQuery(q);
    const url = navigateForQuery(parsed);
    if (url) {
      if (parsed.kind === "flight") addRecent({ type: "flight", value: parsed.value });
      if (parsed.kind === "route") addRecent({ type: "route", from: parsed.from, to: parsed.to });
      navigate(url);
      return;
    }
    if (suggestions[0]) {
      const a = suggestions[0];
      navigate(`/airport/${(a.iata || a.icao).toUpperCase()}`);
    }
  }

  const parsed = parseQuery(q);
  const hint =
    parsed.kind === "flight" ? t("hints.flight") :
    parsed.kind === "route" ? t("hints.route") :
    parsed.kind === "airport" ? t("hints.airport") :
    parsed.kind === "airport-search" ? t("hints.airportSearch") : "";

  const compact = size === "compact";

  return (
    <form onSubmit={submit} className={cn("relative", className)}>
      <div className="flex items-center gap-2 border-b-2 border-foreground/80 pb-2">
        <Search className="h-5 w-5 text-muted-foreground shrink-0 hidden sm:block" />
        <Input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={t("searchPlaceholder")}
          className={cn(
            "border-0 bg-transparent px-0 font-display tracking-tight focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50 min-w-0",
            compact
              ? "h-11 text-base sm:text-lg"
              : "h-12 text-base sm:text-lg md:text-2xl",
          )}
        />
        {hint && (
          <span className="hidden sm:inline-flex shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground font-mono px-2 py-1 rounded bg-secondary">
            {hint}
          </span>
        )}
        <Button type="submit" size="sm" className="shrink-0 h-10 sm:h-9 px-3" aria-label={t("trackAria")}>
          <span className="hidden sm:inline">{t("trackButton")}</span>
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {open && parsed.kind === "airport-search" && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 mt-2 bg-card border border-rule rounded-md shadow-soft overflow-hidden z-20">
          {suggestions.map((a) => (
            <button
              key={a.icao}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => navigate(`/airport/${(a.iata || a.icao).toUpperCase()}`)}
              className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-surface-2 transition-colors"
            >
              <span className="font-mono text-xs w-12 shrink-0">{a.iata || a.icao}</span>
              <span className="flex-1 min-w-0">
                <span className="text-sm truncate block">{a.name}</span>
                <span className="text-xs text-muted-foreground truncate block">
                  {[a.city, a.country].filter(Boolean).join(", ")}
                </span>
              </span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}
    </form>
  );
}